import { useEffect, useState } from 'react';
import { useAppSelector, useAppDispatch } from 'flow/hooks/useRedux';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { List } from 'immutable';

import { getPushtypes, updatePushtypes } from 'flow/actions/extension-actions';
import Loading from 'flow/components/ui-components/Loading';
import RouteLink from 'flow/components/ui-components/RouteLink';
import Tabs from 'flow/components/ui-components/Tabs';
import Toggle from 'flow/components/ui-components/Toggle';
import { useModal } from 'flow/contexts/ModalProvider';
import useAppContext from 'flow/hooks/useAppContext';
import useExtension from 'flow/hooks/useExtension';
import useProfiles from 'flow/hooks/useProfiles';
import useSettings from 'flow/hooks/useSettings';
import useSettingsNavigation from 'flow/hooks/useSettingsNavigation';
import useSinglePaneMode from 'flow/hooks/useSinglePaneMode';
import { selectHasCapability } from 'flow/selectors/ExtensionSelectors';
import { ExtensionKey } from 'flow/types/EntityKeys';
import { PushType } from 'flow/types/Extension';
import { routes } from 'flow/types/routes';

import { TypedMap } from 'flow/types/TypedMap';
import { Device } from 'flow/types/Types';
import { Capabilities } from 'flow/utils/capability-utils';
import {
    getDefaultNotificationSetting,
    removeUnusedProfileSettings,
    variesNotifications,
} from 'flow/utils/notification-utils';
import { generatePath } from 'flow/utils/router-utils';
import Page from './Page';
import SettingRow from './SettingRow';

interface Tab {
    key: Device;
    label: string;
}

const excludedPushTypes = ['incoming_queue_call'];

const Notifications = () => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();
    const history = useHistory();
    const { pathname, search } = useLocation();
    const { openModal } = useModal();
    const { goToSettings } = useSettingsNavigation();
    const { extensionKey } = useParams<{ extensionKey: ExtensionKey }>();
    const hasCapabilitySms = useAppSelector((state) =>
        selectHasCapability(state, extensionKey, Capabilities.SMS),
    );

    const queryParams = new URLSearchParams(search);
    const type: Device = (queryParams.get('type') as Device) || 'desktop';

    const handleUpdateQuery = (newValue: Device) => {
        queryParams.set('type', newValue);

        history.replace({
            pathname,
            search: queryParams.toString(),
        });
    };

    const [device, setDevice] = useState<Device>(type);
    const [perProfile, setPerProfile] = useState(false);
    const [disabledAllToggles, setDisabledAllToggles] = useState(false);
    const [varies, setVaries] = useState(false);

    const { extension } = useExtension(extensionKey);
    const pushTypes = extension.get('pushTypes');
    const allProfiles = useAppSelector((state) => state.profiles);
    const { profiles } = useProfiles(extensionKey);
    const { isSinglePaneMode } = useSinglePaneMode();

    const { activeNotifications } = useSettings(extensionKey);

    const updateVariesNotification = (device: Device) => {
        const profileVary = variesNotifications(
            pushTypes.filter((profiles) =>
                profiles
                    .get('notificationSettings')
                    .some((deviceSettings) => deviceSettings.get(device)),
            ),
            device,
        );
        setPerProfile(profileVary);
        setVaries(profileVary);
        handleUpdateQuery(device);
    };

    useEffect(() => {
        if (!profiles || !pushTypes || !type) {
            return;
        }

        updateVariesNotification(type);
    }, [type, profiles, pushTypes]);

    if (!pushTypes || !profiles.size) {
        return <Loading signalFinishedLoading={true} />;
    }

    const tabs: Tab[] = [
        {
            key: 'desktop',
            label: i18next.t('settings.notifications.device.desktop'),
        },
        {
            key: 'mobile',
            label: i18next.t('settings.notifications.device.mobile'),
        },
        {
            key: 'email',
            label: i18next.t('settings.notifications.device.email'),
        },
        { key: 'sms', label: i18next.t('settings.notifications.device.sms') },
    ];

    const filteredTabs = tabs.filter(
        (tab) => tab.key !== 'sms' || hasCapabilitySms,
    );

    const updateProfile = (updatedMap: List<TypedMap<PushType>>) => {
        setDisabledAllToggles(true);
        dispatch(
            updatePushtypes(extensionKey, updatedMap, () => {
                dispatch(getPushtypes(extensionKey));
                setDisabledAllToggles(false);
            }),
        );
    };

    const setAvailableValue = (pushType: TypedMap<PushType>) => {
        const notificationSettings = pushType.get('notificationSettings');
        if (notificationSettings.size <= 1) return pushType;

        const availableSetting = notificationSettings.find(
            (setting) =>
                setting.getIn(['profile', 'description']) === 'Tillgänglig',
        );

        const deviceValue = availableSetting
            ? availableSetting.get(device)
            : getDefaultNotificationSetting(notificationSettings).get(device);

        const updated = notificationSettings.map((setting) =>
            setting.set(device, deviceValue),
        );
        return pushType.update('notificationSettings', () => updated);
    };

    const handleSubmit = async () => {
        const updatedMap = pushTypes
            .map(setAvailableValue)
            .map(removeUnusedProfileSettings);
        updateProfile(updatedMap);
        setVaries(false);
        setPerProfile(false);
    };

    const handleChange = async (
        pushType: TypedMap<PushType>,
        value: boolean,
    ) => {
        const index = pushTypes.findIndex(
            (type) => type.get('name') === pushType.get('name'),
        );
        const updatedMap = pushTypes.setIn(
            [index, 'notificationSettings', 0, device],
            value,
        );
        updateProfile(updatedMap);
    };

    const toggleProfileHandler = (value: boolean) => {
        if (perProfile && varies) {
            openModal('confirmationDialog', {
                title: i18next.t('settings.reset.warning.general.Title'),
                description: (
                    <div
                        dangerouslySetInnerHTML={{
                            __html: i18next.t(
                                'settings.reset.warning.general',
                                {
                                    currentSettings: i18next.t(
                                        'settings.notifications',
                                    ),
                                    firstProfileName: profiles
                                        .first()
                                        .get('description'),
                                },
                            ),
                        }}
                    />
                ),
                handleAccept: handleSubmit,
            });
        } else {
            setPerProfile(value);
        }
    };

    return (
        <Page
            header="settings.notifications"
            onClickBack={isSinglePaneMode ? goToSettings : undefined}
            footer={
                <>
                    <p>{i18next.t('settings.profile_edit')}</p>
                    <Toggle
                        value={perProfile}
                        onChange={toggleProfileHandler}
                        disabled={disabledAllToggles}
                        text={i18next.t('settings.profile_edit')}
                    />
                </>
            }
        >
            <p className="pt-4 pb-6">
                {i18next.t('settings.notifications_conf')}
            </p>
            <Tabs
                className="mb-3"
                tabs={filteredTabs}
                selectedKey={device}
                onClick={({ key }: Tab) => {
                    setDevice(key);
                    updateVariesNotification(key);
                }}
            />
            {pushTypes
                .filter(
                    (pushType) =>
                        !excludedPushTypes.includes(pushType.get('name')),
                )
                .map((pushType) => {
                    const settings = pushType
                        .get('notificationSettings')
                        .first();
                    if (!settings?.has(device)) return null;

                    return perProfile ? (
                        <RouteLink
                            className="h-13"
                            key={pushType.get('name')}
                            to={generatePath(
                                routes[
                                    'settings.notifications.device.pushType'
                                ],
                                {
                                    extensionKey,
                                    device,
                                    pushType: pushType.get('name'),
                                },
                            )}
                            description={i18next.t(
                                'settings.number_of_profiles',
                                activeNotifications(
                                    allProfiles,
                                    pushType,
                                    device,
                                ),
                            )}
                        >
                            {i18next.t(`settings.${pushType.get('name')}`)}
                        </RouteLink>
                    ) : (
                        <SettingRow
                            key={pushType.get('name')}
                            cta={
                                <Toggle
                                    value={settings.get(device)}
                                    onChange={(value) =>
                                        handleChange(pushType, value)
                                    }
                                    disabled={disabledAllToggles}
                                    text={i18next.t(
                                        `settings.${pushType.get('name')}`,
                                    )}
                                />
                            }
                        >
                            {i18next.t(`settings.${pushType.get('name')}`)}
                        </SettingRow>
                    );
                })}
        </Page>
    );
};

export default Notifications;
